/**
 * Shared Zod validation schemas
 *
 * Centralized validation logic to eliminate duplication across forms
 */

import { z } from "zod";
import { NDC_REGEX, NDC_ERROR_MESSAGE } from "./ndc";
import { validateDateString, isDateInFuture } from "@repo/utils";

/**
 * Common field schemas
 */
export const CommonSchemas = {
  // NDC (National Drug Code)
  ndc: z.string().min(1, "NDC is required").regex(NDC_REGEX, NDC_ERROR_MESSAGE),

  // Item/Product name
  itemName: z
    .string()
    .min(1, "Item name is required")
    .max(200, "Item name must be less than 200 characters"),

  // Drug type/schedule
  drugType: z.enum(
    ["schedule_ii", "schedule_iii", "schedule_iv", "schedule_v"],
    {
      required_error: "Drug type is required",
    }
  ),

  // Quantities
  quantity: z.coerce
    .number()
    .int("Must be a whole number")
    .positive("Must be greater than 0"),

  quantityNonNegative: z.coerce
    .number()
    .int("Must be a whole number")
    .min(0, "Must be 0 or greater"),

  // Stock thresholds
  reorderThreshold: z.coerce
    .number()
    .int("Must be a whole number")
    .min(0, "Must be 0 or greater"),

  // User initials (for login/identification)
  initials: z
    .string()
    .transform((val) => val.trim().toUpperCase())
    .pipe(
      z
        .string()
        .min(2, "Initials must be at least 2 characters")
        .max(5, "Initials must be at most 5 characters")
        .regex(/^[A-Z]{2,5}$/, "Must be 2-5 uppercase letters only")
    ),

  // RX number
  rxNumber: z
    .string()
    .min(1, "RX number is required")
    .max(50, "RX number must be less than 50 characters"),

  // Wholesaler/Vendor
  wholesaler: z
    .string()
    .min(1, "Wholesaler is required")
    .max(200, "Wholesaler name must be less than 200 characters"),

  // Invoice number
  invoiceNumber: z
    .string()
    .min(1, "Invoice number is required")
    .max(100, "Invoice number must be less than 100 characters"),

  // Lot number
  lotNumber: z
    .string()
    .max(100, "Lot number must be less than 100 characters")
    .optional(),

  // Dates (for forms using Date objects)
  date: z.coerce.date({
    required_error: "Date is required",
    invalid_type_error: "Invalid date format",
  }),

  dateOptional: z.coerce
    .date({
      invalid_type_error: "Invalid date format",
    })
    .optional()
    .nullable(),

  // Date strings (YYYY-MM-DD) for API validation
  dateString: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
    .refine((date) => validateDateString(date), {
      message: "Invalid date",
    }),

  // Date string that cannot be in the future
  pastOrTodayDateString: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
    .refine((date) => validateDateString(date), {
      message: "Invalid date",
    })
    .refine((date) => !isDateInFuture(date), {
      message: "Date cannot be in the future",
    }),

  // Notes
  notes: z
    .string()
    .max(1000, "Notes must be less than 1000 characters")
    .optional(),

  // Password for verification
  password: z
    .string()
    .min(4, "Password must be at least 4 characters")
    .max(16, "Password must be no more than 16 characters"),
};

/**
 * Receipt form schema
 */
export const receiptSchema = z.object({
  ndc: CommonSchemas.ndc,
  item_name: CommonSchemas.itemName,
  quantity_received: CommonSchemas.quantity,
  date_received: CommonSchemas.date,
  wholesaler: CommonSchemas.wholesaler,
  invoice_number: CommonSchemas.invoiceNumber,
});

export type ReceiptFormValues = z.infer<typeof receiptSchema>;

/**
 * Dispense form schema
 */
export const dispenseSchema = z.object({
  ndc: CommonSchemas.ndc,
  item_name: CommonSchemas.itemName,
  quantity_dispensed: CommonSchemas.quantity,
  date: CommonSchemas.date,
  rx_number: CommonSchemas.rxNumber,
});

export type DispenseFormValues = z.infer<typeof dispenseSchema>;

/**
 * Inventory/Product form schema
 */
export const inventorySchema = z.object({
  ndc: CommonSchemas.ndc,
  item_name: CommonSchemas.itemName,
  drug_type: CommonSchemas.drugType,
  initial_stock: CommonSchemas.quantityNonNegative,
  reorder_threshold: CommonSchemas.reorderThreshold,
});

export type InventoryFormValues = z.infer<typeof inventorySchema>;

/**
 * Note content schema
 */
export const noteSchema = z.object({
  content: z
    .string()
    .min(1, "Note content is required")
    .max(1000, "Note must be less than 1000 characters"),
  entityType: z.enum(["inventory", "receipt", "dispense", "adjustment"]),
  entityId: z.string().uuid("Invalid entity ID"),
});

export type NoteFormValues = z.infer<typeof noteSchema>;

/**
 * Password verification schema
 */
export const passwordVerificationSchema = z.object({
  password: CommonSchemas.password,
});

export type PasswordVerificationValues = z.infer<
  typeof passwordVerificationSchema
>;

/**
 * User creation schema
 */
export const userSchema = z.object({
  initials: CommonSchemas.initials,

  full_name: z
    .string()
    .min(1, "Full name is required")
    .max(200, "Full name must be less than 200 characters"),

  role: z.enum(
    ["admin", "supervising_pharmacist", "pharmacist", "technician", "auditor"],
    {
      required_error: "Role is required",
    }
  ),

  password: CommonSchemas.password,
});

export type UserFormValues = z.infer<typeof userSchema>;

/**
 * Date range schema for filtering/querying
 */
export const dateRangeSchema = z
  .object({
    startDate: CommonSchemas.dateString,
    endDate: CommonSchemas.dateString,
  })
  .refine((data) => data.startDate <= data.endDate, {
    message: "Start date must be before or equal to end date",
    path: ["startDate"],
  });

export type DateRangeValues = z.infer<typeof dateRangeSchema>;

/**
 * Pharmacy signup API response
 *
 * The backend auto-logs the user in by issuing session cookies, then
 * returns the pharmacy/org IDs plus flags the onboarding flow uses
 * (needsFirstUser / autoLoginFailed). Parsing with Zod removes the
 * ad-hoc Record casts in the signup page.
 */
export const pharmacySignupResponseSchema = z.object({
  success: z.boolean().optional(),
  needsFirstUser: z.boolean().optional(),
  autoLoginFailed: z.boolean().optional(),
  data: z.object({
    email: z.string(),
    pharmacyId: z.string().optional(),
    organizationId: z.string().optional(),
  }),
});

export type PharmacySignupResponse = z.infer<
  typeof pharmacySignupResponseSchema
>;

/**
 * Pharmacy login API response
 *
 * Used by the login page to decide whether to continue to the staff
 * selection screen or jump straight to the dashboard when no staff users
 * exist yet.
 */
export const pharmacyLoginResponseSchema = z.object({
  success: z.boolean().optional(),
  needsFirstUser: z.boolean().optional(),
  requires2fa: z.boolean().optional(),
  pharmacy: z.object({
    id: z.string(),
    legal_name: z.string().nullable().optional(),
    dba_name: z.string().nullable().optional(),
    ncpdp_id: z.string().nullable().optional(),
    subscription_status: z.string().nullable().optional(),
    trial_ends_at: z.string().nullable().optional(),
  }),
});

export type PharmacyLoginResponse = z.infer<typeof pharmacyLoginResponseSchema>;
